// src/components/work/work.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Phone,
  ShieldCheck,
  Globe2,
  Building2,
  Leaf,
  Sparkles,
} from 'lucide-react';
import './work.css';

import img2 from '../../img/environment/water.jpg';
import approachImg from '../../img/environment/fieldwork.jpg';
import kivuwattImg from '../../img/projects/kivuwatt-high-resolution.jpg';
import conventionImg from '../../img/projects/kigali-convention-centre.jpg';
import golfImg from '../../img/projects/kigali-golf-resort.jpg';
import airportImg from '../../img/projects/new-bugesera-airport.jpg';
import rilimaImg from '../../img/projects/rilima-cluster.jpg';
import skylineImg from '../../img/projects/kigali-skyline.jpg';
import wetlandsImg from '../../img/projects/kibumba-wetland-restoration.jpg';
import greenComplexImg from '../../img/projects/kigali-green-complex-client.jpg';

const images = [kivuwattImg, conventionImg, golfImg, airportImg];
const imageDescriptions = [
  'Gas extraction platform on Lake Kivu',
  'Kigali Convention Centre illuminated at dusk',
  'View across Kigali Golf Club',
  'New Bugesera International Airport runway construction',
];

const Work = () => {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);

  // autoplay + progress bar
  useEffect(() => {
    setProgress(0);
    timerRef.current && clearInterval(timerRef.current);
    const step = 100 / 60; // 3s bar (60 ticks)
    let p = 0;
    timerRef.current = setInterval(() => {
      p += step;
      if (p >= 100) {
        p = 0;
        setCurrent((c) => (c + 1) % images.length);
      }
      setProgress(p);
    }, 50);
    return () => clearInterval(timerRef.current);
  }, [current]);

  const next = () => setCurrent((c) => (c + 1) % images.length);
  const prev = () => setCurrent((c) => (c - 1 + images.length) % images.length);

  const principles = [
    { icon: <ShieldCheck size={18} />, title: 'Compliance First', text: 'IFC Performance Standards, World Bank ESF, AfDB OS, and national policy alignment.' },
    { icon: <Globe2 size={18} />, title: 'Regional Reach', text: 'Delivery across Rwanda & the EAC with trusted local partnerships.' },
    { icon: <Leaf size={18} />, title: 'Nature-First', text: 'Cleaner production, circularity, and practical mitigation at the core.' },
    { icon: <Building2 size={18} />, title: 'Real Outcomes', text: 'Stakeholder-ready deliverables that unlock permits and funding.' },
    { icon: <Sparkles size={18} />, title: 'Modern Tools', text: 'Data-driven assessments, GIS, and reproducible methods.' },
  ];

  const cases = [
    {
      img: img2,
      alt: 'Lake and shoreline representing the Lake Kivu project area',
      sector: 'Energy',
      title: 'Shema Power Lake Kivu Project',
      blurb: 'ESIA and RAP work for the 50 MW Lake Kivu gas-to-power development, formerly the Symbion Power project.',
    },
    {
      img: kivuwattImg,
      alt: 'KivuWatt gas extraction platform on Lake Kivu',
      sector: 'Energy',
      title: 'KivuWatt Project',
      blurb: 'Environmental impact assessment for the Lake Kivu methane-gas extraction and power-generation project.',
    },
    {
      img: conventionImg,
      alt: 'Kigali Convention Centre illuminated at dusk',
      sector: 'Urban Development',
      title: 'Kigali Convention Centre',
      blurb: 'Environmental assessment and management planning for the convention centre, hotel, and IT office park.',
    },
    {
      img: skylineImg,
      alt: 'View of the Kigali skyline',
      sector: 'Urban Development',
      title: 'Kigali International Finance and Business Square',
      blurb: 'ESIA for the mixed-use finance and business development comprising hotel, office, retail, and conference facilities.',
    },
    {
      img: golfImg,
      alt: 'Wide view across Kigali Golf Club',
      sector: 'Tourism and Recreation',
      title: 'Kigali Golf Resort',
      blurb: 'ESIA and detailed hydrological study supporting expansion from nine to eighteen holes.',
    },
    {
      img: airportImg,
      alt: 'Aerial view of runway construction at New Bugesera International Airport',
      sector: 'Aviation',
      title: 'New Kigali International Airport (ESIA)',
      blurb: 'Environmental and social impact assessment for the new international airport development in Bugesera.',
    },
    {
      img: rilimaImg,
      alt: 'Rilima school complex and community setting in Bugesera District',
      sector: 'Resettlement',
      title: 'Rilima Cluster and Airport Buffer Zone RAP',
      blurb: 'Implementation of the Resettlement Action Plan for the Rilima Cluster and the airport buffer zone.',
    },
    {
      img: wetlandsImg,
      alt: 'Aerial view of the restored Kibumba Wetland in Kigali',
      sector: 'Urban Resilience',
      title: 'Rehabilitation of Five Kigali Wetlands',
      blurb: 'ESIA and RAP studies for Kibumba, Nyabugogo, Rugenge–Rwintare, Gikondo, and Rwampara wetlands.',
    },
    {
      img: greenComplexImg,
      alt: 'Updated architectural rendering of the Kigali Green Complex Tower',
      sector: 'Green Buildings',
      title: 'Kigali Green Complex Tower',
      blurb: 'Environmental impact assessment for the landmark mixed-use tower in Kigali’s central business district.',
    },
  ];

  return (
    <div className="work-wrap">
      {/* SHOWCASE */}
      <section className="work-showcase">
        <div className="work-glow" aria-hidden="true" />
        <div className="showcase-frame">
          <img src={images[current]} alt={imageDescriptions[current]} />
          <div className="showcase-ui">
            <button className="nav-btn" onClick={prev} aria-label="Previous">
              <ChevronLeft size={22} />
            </button>
            <div className="dots">
              {images.map((_, i) => (
                <span key={i} className={`dot ${i === current ? 'active' : ''}`} />
              ))}
            </div>
            <button className="nav-btn" onClick={next} aria-label="Next">
              <ChevronRight size={22} />
            </button>
          </div>
          <div className="progress">
            <span style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="showcase-caption">
          <h2>Project Experience</h2>
          <p>
            Environmental and social safeguards for major energy, aviation, urban-development, tourism, and
            climate-resilience investments in Rwanda.
          </p>
        </div>
      </section>

      {/* APPROACH */}
      <section className="work-approach">
        <div className="w-container split">
          <div className="split-text">
            <h2 className="w-title">Our Approach</h2>
            <div className="w-underline" />
            <p>
              We operate as an integrated practice: science-based assessments, strong stakeholder engagement,
              and clear, decision-ready outputs. Your goals become ours.
            </p>
            <ul className="principles">
              {principles.map((p) => (
                <li key={p.title}>
                  <span className="chip">{p.icon}</span>
                  <div>
                    <h4>{p.title}</h4>
                    <p>{p.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="split-media">
            <div className="media-frame">
              <img src={approachImg} alt="Ecological research using a microscope in a forest" />
              <span className="corner" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      {/* CASES */}
      <section className="work-cases">
        <div className="w-container">
          <div className="case-heading">
            <p className="w-eyebrow">Our track record</p>
            <h2 className="w-title">Selected Project References</h2>
            <div className="w-underline" />
            <p className="case-intro">Priority assignments highlighted in the 2026 company profile.</p>
          </div>
          <div className="case-grid">
            {cases.map((c, index) => (
              <article key={c.title} className="case-card">
                <img src={c.img} alt={c.alt} />
                <span className="case-number" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="case-overlay">
                  <span className="case-sector">{c.sector}</span>
                  <h3>{c.title}</h3>
                  <p>{c.blurb}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="work-contact">
        <div className="contact-card">
          <h3>Need Help?</h3>
          <p className="accent">Just make a call</p>
          <p className="phone"><Phone size={20} /> +250 788 306 105</p>
          <a className="call-btn" href="tel:+250788306105">Call Now</a>
        </div>
      </section>
    </div>
  );
};

export default Work;
